import { Fragment, useEffect, useMemo, useState } from "react";
import { ICON_BASE_PATH } from "@/app/utils/config";
import { gxnotifyViewApi } from "@/app/apiConfig/services/notificationsAbstractApiLayer";
import type { NotificationItem } from "./notificationData";
import { Loader } from "@progress/kendo-react-indicators";
import NoDataFound from "@/app/components/common/warning/NoDataFound";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { RemarksModal } from "@/app/models/dialogs/RemarksModal";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useRejectRtpRequest } from "../apiHooks/useRejectRtpRequest";
import { useDispatch } from "react-redux";
import type { AppDispatch } from "@/app/redux/store";
import { setFilteredNotifications } from "@/app/redux/reducers/notificationsMessage";
import { handleNotification } from "@/app/redux/reducers/userSlice";
import DialogMini from "@/app/models/dialogs/DialogMini";
import { NotificationDetails } from "./NotificationDetails";

const categoryIconFile: Record<string, string> = {
  Payments: "payment-icon-teal.svg",
  Compliance: "shield-icon-orange.svg",
  Accounts: "file-icon-purple.svg",
  FX: "exchange-icon-blue.svg",
  Approval: "approval-icon-yellow.svg",
  Receivables: "wallet-icon-green.svg",
  Platform: "settings-icon-gray.svg",
};

const sectionOrder = ["Today", "Yesterday", "This Week", "Earlier"] as const;

interface NotificationViewProps {
  activeTab: string;
  unreadOnly: boolean;
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onVisibleChange?: (ids: string[]) => void;
}

export const NotificationView = ({
  activeTab,
  unreadOnly,
  selectedIds,
  onToggleSelect,
  onVisibleChange,
}: NotificationViewProps) => {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const naviagte = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [rejectReuqestId, setRejectReuqestId] = useState<number | null>(null);
  const [openRemarkModal, setOpenRemarkModal] = useState<boolean>(false);
  const { rejectRequestApi } = useRejectRtpRequest();
  const [selectedNotification, setSelectedNotification] =
    useState<NotificationItem | null>(null);
  const [openViewNotification, setOpenViewNotification] =
    useState<boolean>(false);
  const {
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    reset,
  } = useForm<any>({
    defaultValues: {
      reason: "",
    },
    resolver: yupResolver(
      yup.object().shape({
        reason: yup.string().required("Please enter the reject reason"),
      }),
    ),
  });

  const getSection = (dateString: string) => {
    const notificationDate = new Date(dateString);
    const today = new Date();

    const startOfToday = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
    );

    const startOfNotification = new Date(
      notificationDate.getFullYear(),
      notificationDate.getMonth(),
      notificationDate.getDate(),
    );

    const diffDays =
      (startOfToday.getTime() - startOfNotification.getTime()) /
      (1000 * 60 * 60 * 24);

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays <= 7) return "This Week";
    return "Earlier";
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString)
      .toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
      .toLocaleUpperCase();
  };

  const fetchnotifyView = async () => {
    let status = "";
    if (unreadOnly) {
      status = "UNREAD";
    } else if (activeTab === "Archives") {
      status = "ARCHIVE";
    }
    const type =
      activeTab === "All" || activeTab === "Archives" ? "" : activeTab;
    const payload = {
      pageNo: 1,
      pageSize: 10,
      type,
      status,
    };
    try {
      setLoading(true);
      const response: any = await gxnotifyViewApi(payload);
      const mappedNotifications = response.data.map((item: any) => ({
        id: String(item.notificationHistId),
        title: item.notificationSubject,
        description: item.notificationContent,
        category: item.categoryType,
        time: item.time,
        unread: item.notificationStatus === "UNREAD",
        actionRequired: item.actionRequired === "Y",
        rtpActionFlag: item.rtpActionFlag,
        actionId: item.referenceEntityId,
        section: getSection(item.time),
      }));
      setNotifications(mappedNotifications);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchnotifyView();
  }, [activeTab, unreadOnly]);

  const filteredNotifications = useMemo(() => {
    return notifications.filter((item) => {
      const matchesUnread = unreadOnly ? item.unread : true;
      return matchesUnread;
    });
  }, [notifications, unreadOnly, activeTab]);

  const groups = useMemo(() => {
    return sectionOrder
      .map((section) => ({
        section,
        items: filteredNotifications.filter((item) => item.section === section),
      }))
      .filter((group) => group.items.length > 0);
  }, [filteredNotifications, sectionOrder]);

  useEffect(() => {
    dispatch(setFilteredNotifications(filteredNotifications));
  }, [filteredNotifications, dispatch]);

  const visibleIds = useMemo(() => {
    return filteredNotifications.map((n) => n.id);
  }, [filteredNotifications]);

  useEffect(() => {
    onVisibleChange?.(visibleIds);
  }, [visibleIds, onVisibleChange]);

  const handleAcceptRequest = () => {
    if (selectedNotification?.actionId) {
      naviagte("/payments/initiate-new-transfer/domestic-transfer", {
        state: {
          from: "notification",
          rtpTransId: selectedNotification?.actionId,
        },
      });
      dispatch(handleNotification());
    }
  };

  const handleRejectRequest = () => {
    setOpenRemarkModal(true);
    if (selectedNotification?.actionId) {
      setRejectReuqestId(selectedNotification?.actionId);
    }
  };

  const closeRemarkModal = () => {
    setOpenRemarkModal(false);
    setSelectedNotification(null);
    setOpenViewNotification(false);
    reset();
  };

  const handleRemark = (value: string) => {
    setValue("reason", value);
  };

  const onSubmit = async (data: { reason: string }) => {
    await rejectRequestApi({
      rtpTransId: rejectReuqestId,
      remarks: data?.reason,
    });
    closeRemarkModal();
    fetchnotifyView();
  };

  const handleViewNotification = () => {
    setOpenViewNotification((prev) => !prev);
  };

  const handleSelectNotification = (item: NotificationItem) => {
    setSelectedNotification(item);
  };
  useEffect(() => {
    if (selectedNotification) {
      if (selectedNotification?.rtpActionFlag === "Y") {  //if you want to view for all like payment , account should remove this condition
        handleViewNotification();
      }
    }
  }, [selectedNotification]);

  useEffect(() => {
    if (!openViewNotification) {
      setSelectedNotification(null);
    }
  }, [openViewNotification]);
  return (
    <>
      {loading ? (
        <div className="gx-converging-spinner">
          <Loader size="large" type="converging-spinner" />
        </div>
      ) : (
        <>
          {groups.length !== 0 ? (
            <>
              <div className="gx-notification-table-wrapper">
                <table className="notification-table">
                  <thead>
                    <tr>
                      <th className="gx-p12-b">Notifications</th>
                      <th className="gx-p12-b">Category</th>
                      <th className="gx-p12-b">Time</th>
                      <th></th>
                      <th></th>
                    </tr>
                  </thead>

                  <tbody>
                    {groups?.map((group) => (
                      <Fragment key={group.section}>
                        <tr className="notification-table__group-row">
                          <td
                            colSpan={5}
                            className="notification-table-group gx-p12-b"
                          >
                            {group.section}
                          </td>
                        </tr>

                        {group?.items?.map((item) => {
                          const iconFile =
                            categoryIconFile[item.category] ||
                            "settings-icon-gray.svg";

                          const categorySlug = item.category
                            .toLowerCase()
                            .replace(/\s+/g, "-");

                          return (
                            <tr
                              key={item.id}
                              onClick={() => handleSelectNotification(item)}
                              className={`notification-row ${item.unread ? "notification-row--unread" : ""}`}
                            >
                              <td>
                                <div className="notification-cell">
                                  <span
                                    className={`notification-dot ${item.unread ? "UNREAD" : ""}`}
                                  />

                                  <span
                                    className={`notification-icon notification-icon--${categorySlug}`}
                                  >
                                    <img
                                      src={`${ICON_BASE_PATH}${iconFile}`}
                                      alt={item.category}
                                    />
                                  </span>

                                  <div className="notification-cell__body">
                                    <div className="notification-cell__title-row">
                                      <h2 className="notification-cell__title gx-p14-b">
                                        {item.title}
                                      </h2>

                                      {item.actionRequired && (
                                        <span className="notification-action-required">
                                          Action Required
                                        </span>
                                      )}
                                    </div>

                                    <p
                                      className="notification-cell__description gx-p12-md"
                                      data-tooltip={item.description}
                                    >
                                      {item.description}
                                    </p>
                                  </div>
                                </div>
                              </td>

                              <td>
                                <span
                                  className={`notification-category gx-p14-sb notification-category--${categorySlug}`}
                                >
                                  {item.category}
                                </span>
                              </td>

                              <td className="notification-time gx-p12-m">
                                {formatTime(item.time)}
                              </td>

                              {/* <td>
                                {item?.rtpActionFlag === "Y" && (
                                  <div className="gx-notification-list-card-buttons">
                                    <button
                                      type="button"
                                      onClick={() => handleAcceptRequest(item)}
                                      className="gx-notification-accept-button"
                                    >
                                      <span className="gx-p12-b">Accept</span>
                                    </button>

                                    <button
                                      type="button"
                                      onClick={() => handleRejectRequest(item)}
                                      className="gx-notification-reject-button"
                                    >
                                      <span className="gx-p12-b">Reject</span>
                                    </button>
                                  </div>
                                )}
                              </td> */}

                              <td className="notification-checkbox-cell">
                                <input
                                  type="checkbox"
                                  className="notification-checkbox"
                                  checked={selectedIds.has(item.id)}
                                  onChange={() => onToggleSelect(item.id)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>

              {openRemarkModal && (
                <RemarksModal
                  errors={errors}
                  setValue={setValue}
                  value={watch("reason")}
                  isReject={true}
                  isOpen={openRemarkModal}
                  onClose={closeRemarkModal}
                  onChange={handleRemark}
                  submit={handleSubmit(onSubmit)}
                />
              )}

              <DialogMini
                open={openViewNotification}
                close={handleViewNotification}
                title={
                  <div className="gx-notification-view-modal-title">
                    <span
                      className={`notification-icon notification-icon--${selectedNotification?.category
                        .toLowerCase()
                        .replace(/\s+/g, "-")}`}
                    >
                      <img
                        src={`${ICON_BASE_PATH}${
                          categoryIconFile[
                            selectedNotification?.category || ""
                          ] || "settings-icon-gray.svg"
                        }`}
                        alt={selectedNotification?.category}
                      />
                    </span>
                    <h2 className="notification-cell__title gx-p16-sb">
                      {selectedNotification?.title}
                    </h2>
                  </div>
                }
                primaryBtnLabel="Accept"
                secondaryBtnLabel="Reject"
                primaryBtn={handleAcceptRequest}
                secondaryBtn={handleRejectRequest}
              >
                <NotificationDetails item={selectedNotification} />
              </DialogMini>
            </>
          ) : (
            <NoDataFound message="Data Not Found" />
          )}
        </>
      )}
    </>
  );
};
